import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Plus, Edit, Calendar, FileDown, Loader2, Users, Archive, ClipboardCheck, Award, Search, Trash2, BookOpenCheck, ChevronRight, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

const LessonsPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get('tab');
    const activeTab = (urlTab === 'poe' || urlTab === 'lessons') ? urlTab : (localStorage.getItem('lessonsActiveTab') === 'poe' ? 'poe' : 'lessons');
    const [lessons, setLessons] = useState([]);
    const [criteria, setCriteria] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedYear, setSelectedYear] = useState('all');
    const [search, setSearch] = useState('');
    const [poeUnits, setPoeUnits] = useState([]);

    const [formData, setFormData] = useState({
        title: '', description: '', year: '1',
        date: format(new Date(), 'yyyy-MM-dd'), criteria_ids: [],
        components: ['attendance', 'grading']
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [lRes, cRes] = await Promise.all([
                    axios.get(`${API}/lessons`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/criteria`, { headers: getAuthHeaders() })
                ]);
                setLessons(lRes.data);
                setCriteria(cRes.data);
            } catch (e) { toast.error('Failed to load'); }
            finally { setLoading(false); }
            try {
                const pRes = await axios.get(`${API}/poe/template`, { headers: getAuthHeaders() });
                setPoeUnits(pRes.data.units || []);
            } catch (e) { /* POE template optional */ }
        };
        load();
    }, []);

    useEffect(() => {
        localStorage.setItem('lessonsActiveTab', activeTab);
        if (urlTab !== activeTab) setSearchParams({ tab: activeTab }, { replace: true });
    }, [activeTab, urlTab]);

    const switchTab = (tab) => { setSearch(''); setSearchParams({ tab }); };

    const openModal = (lesson = null) => {
        if (lesson) {
            setEditingLesson(lesson);
            setFormData({ title: lesson.title, description: lesson.description || '', year: lesson.year.toString(), date: lesson.date, criteria_ids: lesson.criteria_ids || [], components: lesson.components || ['attendance', 'grading'] });
        } else {
            setEditingLesson(null);
            setFormData({ title: '', description: '', year: '1', date: format(new Date(), 'yyyy-MM-dd'), criteria_ids: [], components: ['attendance', 'grading'] });
        }
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.title) { toast.error('Please enter a lesson title'); return; }
        if (poeTitleMatches.length > 0) {
            const names = poeTitleMatches.map(u => `${u.module_name || u.module_ref} · ${u.unit_no}: ${u.unit_name}`).join('\n');
            if (!window.confirm(`This title matches an existing POE unit:\n\n${names}\n\nPOE units are graded on the Portfolio of Evidence tab — creating a general lesson here may duplicate it.\n\nCreate the lesson anyway?`)) return;
        }
        setSaving(true);
        try {
            const payload = { ...formData, year: parseInt(formData.year) };
            if (editingLesson) {
                await axios.put(`${API}/lessons/${editingLesson.id}`, payload, { headers: getAuthHeaders() });
                toast.success('Lesson updated');
            } else {
                await axios.post(`${API}/lessons`, payload, { headers: getAuthHeaders() });
                toast.success('Lesson created');
            }
            setModalOpen(false);
            const res = await axios.get(`${API}/lessons`, { headers: getAuthHeaders() });
            setLessons(res.data);
        } catch (error) { toast.error('Failed to save lesson'); }
        finally { setSaving(false); }
    };

    const handleArchive = async (lesson, e) => {
        e.stopPropagation();
        try {
            await axios.post(`${API}/lessons/${lesson.id}/archive`, {}, { headers: getAuthHeaders() });
            setLessons(prev => prev.filter(l => l.id !== lesson.id));
            toast.success(`"${lesson.title}" archived`, {
                action: {
                    label: 'Undo', onClick: async () => {
                        try {
                            await axios.post(`${API}/lessons/${lesson.id}/restore`, {}, { headers: getAuthHeaders() });
                            const res = await axios.get(`${API}/lessons`, { headers: getAuthHeaders() });
                            setLessons(res.data);
                            toast.success('Lesson restored');
                        } catch { toast.error('Failed to restore'); }
                    }
                }
            });
        } catch (error) { toast.error('Failed to archive'); }
    };

    const handleDelete = async (lesson, e) => {
        e.stopPropagation();
        try {
            const res = await axios.delete(`${API}/lessons/${lesson.id}`, { headers: getAuthHeaders() });
            if (res.data.requires_confirmation) {
                if (!window.confirm(`${res.data.message}\n\nAre you sure you want to permanently delete this lesson?`)) return;
                await axios.delete(`${API}/lessons/${lesson.id}?confirm=true`, { headers: getAuthHeaders() });
            }
            toast.success('Lesson deleted permanently');
            setLessons(prev => prev.filter(l => l.id !== lesson.id));
        } catch (error) { toast.error('Failed to delete'); }
    };

    const handleExportPDF = async (lesson, e) => {
        e.stopPropagation();
        try {
            const response = await axios.get(`${API}/reports/lesson/${lesson.id}/pdf`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `lesson_report_${lesson.title.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Report downloaded');
        } catch (error) { toast.error('Failed to export'); }
    };

    const toggleCriterion = (cid) => {
        setFormData(prev => ({ ...prev, criteria_ids: prev.criteria_ids.includes(cid) ? prev.criteria_ids.filter(id => id !== cid) : [...prev.criteria_ids, cid] }));
    };

    const toggleComponent = (comp) => {
        setFormData(prev => {
            const has = prev.components.includes(comp);
            const newComps = has ? prev.components.filter(c => c !== comp) : [...prev.components, comp];
            if (newComps.length === 0) return prev;
            return { ...prev, components: newComps };
        });
    };

    const lessonsByYear = { 1: [], 2: [], 3: [] };
    const filteredLessons = lessons.filter(l => {
        const matchesYear = selectedYear === 'all' || l.year === Number(selectedYear);
        const matchesSearch = !search || l.title.toLowerCase().includes(search.toLowerCase());
        return matchesYear && matchesSearch;
    });
    filteredLessons.forEach(l => { if (lessonsByYear[l.year]) lessonsByYear[l.year].push(l); });
    const yearsToShow = selectedYear === 'all' ? [1, 2, 3] : [Number(selectedYear)];

    const poeSearch = search.toLowerCase();
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const titleNorm = norm(formData.title);
    const titleUnitNo = titleNorm.match(/^unit (\d+)\b/);
    const poeTitleMatches = titleNorm.length < 4 ? [] : poeUnits.filter(u => {
        const n = norm(u.unit_name);
        if (n.length >= 6 && titleNorm.includes(n)) return true;
        return !!titleUnitNo && String(u.unit_ref) === titleUnitNo[1];
    });

    const filteredPoeUnits = poeUnits.filter(u => !poeSearch ||
        `${u.unit_no || ''} ${u.unit_name || ''} ${u.evidence_reference || ''} ${u.module_name || ''}`.toLowerCase().includes(poeSearch));

    const poeByModule = [];
    filteredPoeUnits.forEach(u => {
        let g = poeByModule.find(x => x.ref === u.module_ref);
        if (!g) { g = { ref: u.module_ref, name: u.module_name || u.module_ref, units: [] }; poeByModule.push(g); }
        g.units.push(u);
    });

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="lessons-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Lessons</h1>
                        <p className="text-gray-500 mt-1">Manage practical cooking sessions</p>
                    </div>
                    {activeTab === 'lessons' && (
                        <Button onClick={() => openModal()} data-testid="add-lesson-btn" >
                            <Plus className="w-4 h-4 mr-2" /> Add Lesson
                        </Button>
                    )}
                </div>

                {/* Tabs */}
                <TabBar
                    tabs={[
                        { id: 'lessons', label: 'Lessons', icon: Calendar, count: lessons.length },
                        { id: 'poe', label: 'Portfolio of Evidence', icon: BookOpenCheck, count: poeUnits.length }
                    ]}
                    active={activeTab} onChange={switchTab} testIdPrefix="tab" className="lessons-tabs" />

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={activeTab === 'poe' ? 'Search POE units...' : 'Search lessons...'} className="pl-10" data-testid="search-lessons" />
                    </div>
                    {activeTab === 'lessons' && (
                        <div className="flex gap-2">
                            {['all', '1', '2', '3'].map(y => (
                                <button key={y} onClick={() => setSelectedYear(y)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedYear === y ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    data-testid={`year-filter-${y}`}>
                                    {y === 'all' ? 'All' : `Year ${y}`}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* POE Units - grade per the Portfolio of Evidence */}
                {activeTab === 'poe' && (
                    <div className="space-y-1" data-testid="poe-units-section">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 bg-ica-gold rounded-lg flex items-center justify-center">
                                <BookOpenCheck className="w-4 h-4 text-white" />
                            </div>
                            <h2 className="text-lg font-semibold text-ica-navy dark:text-ica-light-gold">POE Units</h2>
                            <span className="text-xs text-gray-400">Grade outcomes directly into each student's Portfolio of Evidence</span>
                        </div>
                        <div className="space-y-4">
                            {poeByModule.map(group => (
                                <div key={group.ref} className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden" data-testid={`poe-module-${group.ref}`}>
                                    <div className="px-5 py-2.5 bg-gray-50 dark:bg-slate-700/40 border-b border-gray-100 dark:border-slate-700">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-ica-navy dark:text-ica-light-gold">{group.name} <span className="text-gray-400 font-normal normal-case">· {group.units.length} units</span></p>
                                    </div>
                                    <div className="divide-y divide-gray-50 dark:divide-slate-700">
                                        {group.units.map(u => (
                                            <div key={u.id} onClick={() => navigate(`/poe/unit/${u.id}`)} data-testid={`poe-unit-row-${u.module_ref}-${u.unit_ref}`}
                                                className="flex items-center px-5 py-4 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-700/30 group">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        {u.evidence_reference && <span className="text-xs font-mono px-2 py-0.5 rounded bg-ica-navy/10 text-ica-navy dark:text-ica-light-gold">{u.evidence_reference}</span>}
                                                        <p className="font-medium text-gray-900 dark:text-slate-100 truncate group-hover:text-ica-navy dark:group-hover:text-ica-gold transition-colors">{u.unit_no}: {u.unit_name}</p>
                                                    </div>
                                                </div>
                                                <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/poe/unit/${u.id}`); }} className="flex-shrink-0 ml-4" data-testid={`poe-unit-grade-${u.module_ref}-${u.unit_ref}`}>
                                                    <Award className="w-4 h-4 mr-1" /> Grade <ChevronRight className="w-4 h-4 ml-0.5" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {poeByModule.length === 0 && (
                                <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 px-5 py-10 text-center" data-testid="poe-empty-state">
                                    <p className="text-sm text-gray-400">{poeUnits.length === 0 ? 'No POE units configured yet. Import a curriculum from POE Setup.' : 'No POE units match your search.'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'lessons' && (loading ? (
                    <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
                ) : (
                    yearsToShow.map(year => (
                        <div key={year} className="space-y-1" data-testid={`year-${year}-group`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-ica-navy rounded-lg flex items-center justify-center">
                                    <span className="text-white text-sm font-bold">{year}</span>
                                </div>
                                <h2 className="text-lg font-semibold text-ica-navy">Year {year}</h2>
                                <span className="text-xs text-gray-400">{lessonsByYear[year].length} lesson{lessonsByYear[year].length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                {lessonsByYear[year].length > 0 ? (
                                    <div className="divide-y divide-gray-50">
                                        {lessonsByYear[year].map(lesson => (
                                            <div key={lesson.id} onClick={() => navigate(`/lessons/${lesson.id}`)} data-testid={`lesson-row-${lesson.id}`}
                                                className="flex items-center px-5 py-4 cursor-pointer transition-all hover:bg-gray-50 group">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-gray-900 truncate group-hover:text-ica-navy transition-colors">{lesson.title}</p>
                                                    <div className="flex gap-1.5 mt-1">
                                                        {(lesson.components || ['attendance', 'grading']).map(c => (
                                                            <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-ica-navy/10 text-ica-navy font-medium">
                                                                {c === 'attendance' ? 'Attendance' : 'Grading'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-6 flex-shrink-0 ml-4">
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500"><Calendar className="w-3.5 h-3.5" />{lesson.date}</div>
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500"><Users className="w-3.5 h-3.5" />{lesson.enrolled_student_ids?.length || 0}</div>
                                                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openModal(lesson); }} className="text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold h-8 w-8 p-0" data-testid={`edit-lesson-${lesson.id}`} title="Edit Lesson">
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={(e) => handleExportPDF(lesson, e)} className="text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold h-8 w-8 p-0" title="Export PDF">
                                                            <FileDown className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={(e) => handleArchive(lesson, e)} className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" data-testid={`archive-lesson-${lesson.id}`} title="Archive Lesson">
                                                            <Archive className="w-4 h-4" />
                                                        </Button>
                                                        {isAdmin() && (
                                                            <Button variant="ghost" size="sm" onClick={(e) => handleDelete(lesson, e)} className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" title="Delete Lesson Permanently">
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="px-5 py-8 text-center"><p className="text-sm text-gray-400">No lessons for Year {year}</p></div>
                                )}
                            </div>
                        </div>
                    ))
                ))}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-lg" data-testid="lesson-modal">
                    <DialogTopBar title={<>{editingLesson ? 'Edit Lesson' : 'New Lesson'}</>}>
    <Button size="sm" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="save-lesson-btn" >
                            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingLesson ? 'Update Lesson' : 'Create Lesson'}
                        </Button>
</DialogTopBar>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Title *</Label>
                            <Input data-testid="lesson-title-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. French Cuisine Basics" />
                            {poeTitleMatches.length > 0 && (
                                <div className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40 px-3 py-2" data-testid="poe-duplicate-warning">
                                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-xs text-amber-800 dark:text-amber-200">
                                        <p className="font-semibold">This matches an existing POE unit</p>
                                        {poeTitleMatches.slice(0, 3).map(u => (
                                            <p key={u.id} className="font-mono">{u.evidence_reference || u.module_ref} · {u.unit_no}: {u.unit_name}</p>
                                        ))}
                                        <p className="mt-1">Grade POE units on the <strong>Portfolio of Evidence</strong> tab instead of creating a duplicate lesson.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description..." rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Year *</Label>
                                <Select value={formData.year} onValueChange={(v) => setFormData({ ...formData, year: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">Year 1</SelectItem>
                                        <SelectItem value="2">Year 2</SelectItem>
                                        <SelectItem value="3">Year 3</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Components *</Label>
                            <div className="flex gap-2">
                                {[{ id: 'attendance', label: 'Attendance', icon: ClipboardCheck }, { id: 'grading', label: 'Grading', icon: Award }].map(c => (
                                    <button key={c.id} onClick={() => toggleComponent(c.id)} data-testid={`toggle-${c.id}`}
                                        className={`flex-1 flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-xs ${formData.components.includes(c.id) ? 'border-ica-gold bg-ica-gold/5 text-ica-navy font-semibold' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                                        <c.icon className="w-3.5 h-3.5" />{c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {formData.components.includes('grading') && (
                            <div className="space-y-2">
                                <Label>Grading Criteria</Label>
                                <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                                    {criteria.map(c => (
                                        <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={formData.criteria_ids.includes(c.id)} onChange={() => toggleCriterion(c.id)} className="rounded border-gray-300 text-ica-gold focus:ring-ica-gold" />
                                            <span className="text-sm text-gray-700">{c.name}</span>
                                        </label>
                                    ))}
                                    {criteria.length === 0 && <p className="text-sm text-gray-400">No criteria defined. Create some in the Criteria page.</p>}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default LessonsPage;
